import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { SectionHeading } from "../components/SectionHeading";
import { Reveal } from "../components/motion";
import { IMAGES } from "../lib/content";
import { Seo } from "../components/Seo";

const WHY = [
  { title: "Reliable Project Opportunities", body: "Gain access to quality multifamily construction projects throughout Southern California." },
  { title: "Transparent Communication", body: "Clear expectations, organized coordination, and responsive project management." },
  { title: "Professional Scheduling", body: "Well-planned schedules that allow every trade partner to perform efficiently." },
  { title: "Safety-First Environment", body: "Every project is managed with OSHA-compliant safety standards." },
  { title: "Long-Term Relationships", body: "We focus on building lasting partnerships based on trust and mutual success." },
];

const QUALIFICATIONS = [
  "Proven industry experience",
  "Strong commitment to safety",
  "High-quality workmanship",
  "Professional communication",
  "Reliable scheduling",
  "Proper licensing",
  "Active insurance coverage",
  "Collaborative team attitude",
];

const PREQUAL = [
  "Company Name",
  "Contact Name",
  "Email Address",
  "Phone Number",
  "Company Address",
  "California Contractor License Number",
  "License Classification",
  "Years in Business",
  "Primary Trades",
  "Service Areas",
  "Number of Employees",
  "Bonding Capacity",
  "Insurance Carrier",
  "Certificate of Insurance",
  "W-9",
  "Company Profile (PDF)",
  "Additional Comments",
];

const PROCESS = [
  { label: "Step 1", title: "Submit Company Information", body: "Complete the Trade Partner application with all required documentation." },
  { label: "Step 2", title: "Qualification Review", body: "Our team reviews licensing, insurance, experience, and qualifications." },
  { label: "Step 3", title: "Approval", body: "Approved companies become part of our qualified trade partner network." },
  { label: "Step 4", title: "Bid Opportunities", body: "Receive invitations to bid on upcoming multifamily projects." },
  { label: "Step 5", title: "Build Together", body: "Work alongside our project teams to deliver exceptional construction projects." },
];

const TRADES = [
  "Concrete", "Structural Steel", "Wood Framing", "Roofing", "Drywall", "Painting",
  "Electrical", "Plumbing", "HVAC", "Fire Sprinklers", "Waterproofing", "Stucco",
  "Windows & Glazing", "Flooring", "Cabinets & Millwork", "Finish Carpentry",
  "Landscaping", "Site Utilities", "Earthwork & Grading", "Demolition",
];

export default function TradePartners() {
  return (
    <>
      <Seo
        title="Trade Partners | Next Horizon Builders"
        description="Partner with Next Horizon Builders. We collaborate with qualified subcontractors and suppliers on multifamily construction projects across Southern California."
        path="/trade-partners"
      />
      <PageHero
        eyebrow="Trade Partners"
        titleLines={["Trade Partners"]}
        tagline="Building Strong Partnerships That Build Better Projects"
        subtitle="At Next Horizon Builders, we believe exceptional projects are built through strong partnerships. We are always looking to collaborate with qualified subcontractors and suppliers who share our commitment to quality, safety, integrity, and craftsmanship."
        cta={{ label: "Become a Trade Partner", to: "/contact" }}
        image={IMAGES.construction[3]}
        imageAlt="Next Horizon Builders multifamily construction site in Southern California"
      />

      {/* Introduction */}
      <section className="bg-white py-24 md:py-36">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <SectionHeading eyebrow="Partner With Us" title="Join Our Trade Partner Network" />
          </div>
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-darkgray/75 text-lg leading-relaxed font-light">
                We value long-term relationships with skilled trade professionals who consistently deliver outstanding work.
                Whether you are a subcontractor, specialty contractor, or material supplier, we welcome the opportunity to
                work together on future multifamily construction projects throughout Southern California.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Why partner with us */}
      <section className="bg-lightgray py-24 md:py-36">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <SectionHeading eyebrow="Why Partner With Us" title="The advantages of building with us." />
          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-200">
            {WHY.map((b, i) => (
              <Reveal key={b.title} delay={(i % 3) * 0.08}>
                <div className="group bg-white p-9 md:p-11 h-full hover:bg-navy transition-colors duration-500">
                  <span className="font-serif text-2xl text-gold">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-8 font-serif font-light text-2xl md:text-3xl text-navy group-hover:text-white transition-colors duration-500 tracking-tight">{b.title}</h3>
                  <p className="mt-4 text-sm text-darkgray/60 group-hover:text-white/60 leading-relaxed font-light transition-colors duration-500">{b.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Qualifications */}
      <section className="bg-white py-24 md:py-36">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10 lg:gap-20 items-start">
          <div className="lg:col-span-4">
            <SectionHeading eyebrow="Qualifications" title="What we look for." subtitle="We look for trade partners who demonstrate:" />
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-x-10 gap-y-5">
            {QUALIFICATIONS.map((q, i) => (
              <Reveal key={q} delay={(i % 2) * 0.06}>
                <div className="flex items-start gap-3 border-b border-gray-100 pb-4">
                  <Check size={18} className="text-gold shrink-0 mt-1" />
                  <span className="text-darkgray font-light">{q}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Prequalification requirements */}
      <section className="bg-lightgray py-24 md:py-36">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <SectionHeading
            eyebrow="Prequalification"
            title="Information we request."
            subtitle="To join our network, applicants are asked to provide the following information and documentation:"
          />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-4">
            {PREQUAL.map((item, i) => (
              <Reveal key={item} delay={(i % 3) * 0.05}>
                <div className="flex items-center gap-3 border-b border-gray-200 pb-3">
                  <span className="font-serif text-gold text-sm w-6 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-darkgray font-light">{item}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Application process */}
      <section className="bg-navy grain py-24 md:py-36 relative overflow-hidden">
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-12">
          <SectionHeading light eyebrow="Application Process" title="How to join our network." />
          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
            {PROCESS.map((s, i) => (
              <Reveal key={s.label} delay={(i % 3) * 0.08}>
                <div className="bg-navy p-9 md:p-10 h-full">
                  <span className="font-serif text-4xl text-gold">{String(i + 1).padStart(2, "0")}</span>
                  <p className="mt-6 text-[0.7rem] uppercase tracking-[0.2em] text-white/40 font-semibold">{s.label}</p>
                  <h3 className="mt-2 font-serif font-light text-2xl text-white">{s.title}</h3>
                  <p className="mt-3 text-white/60 leading-relaxed font-light text-sm">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Trades we frequently seek */}
      <section className="bg-white py-24 md:py-36">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <SectionHeading eyebrow="Scopes We Contract" title="Trades we frequently seek." />
          <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-gray-200 border border-gray-200">
            {TRADES.map((t, i) => (
              <Reveal key={t} delay={(i % 4) * 0.05}>
                <div className="bg-white hover:bg-lightgray transition-colors duration-300 p-8 h-full flex items-center">
                  <span className="font-serif font-light text-xl md:text-2xl text-navy">{t}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy py-24 md:py-32">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 flex flex-col md:flex-row md:items-center md:justify-between gap-10">
          <div>
            <p className="eyebrow mb-5">Become a Partner</p>
            <h2 className="font-serif font-light text-4xl md:text-5xl text-white tracking-tight max-w-xl leading-tight">
              Let's Build the Future Together
            </h2>
            <p className="mt-5 text-white/60 max-w-lg font-light leading-relaxed">
              Become a trusted trade partner of Next Horizon Builders and help us deliver exceptional multifamily communities across Southern California.
            </p>
          </div>
          <Reveal delay={0.1}>
            <Link
              to="/contact"
              data-testid="partner-cta"
              className="group inline-flex items-center gap-3 bg-gold text-white px-9 py-5 text-xs font-semibold uppercase tracking-[0.2em] hover:bg-gold-dark transition-colors duration-300 whitespace-nowrap"
            >
              Become a Trade Partner
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
